import { useState, useEffect, useCallback } from 'react';
import { CsvImportConfigControllerService } from '../api/services/CsvImportConfigControllerService';
import type { CsvImportConfigDto } from '../api/models/CsvImportConfigDto';
import { useErrorHandler } from '../utils/errorHandler';

export interface CsvImportConfigHook {
  config: CsvImportConfigDto | null;
  isLoading: boolean;
  error: Error | null;
  reload: () => Promise<void>;
}

/**
 * Hook to load CSV import configuration for a specific entity type
 * 
 * @param entityType - The entity type (e.g., "materials", "sheeters", "demands")
 * @returns Configuration object, loading state, error, and reload function
 */
export function useCsvImportConfig(entityType: string): CsvImportConfigHook {
  const [config, setConfig] = useState<CsvImportConfigDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { handleError } = useErrorHandler();

  const loadConfig = useCallback(async () => {
    if (!entityType) {
      setConfig(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const loadedConfig = await CsvImportConfigControllerService.getConfigByEntityType(entityType);
      setConfig(loadedConfig);
    } catch (err) {
      // 404 = 'no config for this entity type' - expected, not a user-facing error.
      // The hook then returns config=null and the caller (for example PlanningHorizon) hides
      // the import actions.
      const status = (err as { status?: number })?.status;
      if (status === 404) {
        setConfig(null);
      } else {
        const error = err instanceof Error ? err : new Error('Failed to load CSV config');
        setError(error);
        handleError(err, `loading CSV config for ${entityType}`);
        setConfig(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, [entityType, handleError]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  return {
    config,
    isLoading,
    error,
    reload: loadConfig,
  };
}

/**
 * Hook to get all CSV import configurations
 */
export function useAllCsvImportConfigs() {
  const [configs, setConfigs] = useState<CsvImportConfigDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { handleError } = useErrorHandler();

  const loadConfigs = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const loadedConfigs = await CsvImportConfigControllerService.findAll();
      setConfigs(loadedConfigs);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load CSV configs');
      setError(error);
      handleError(err, 'loading CSV configs');
      setConfigs([]);
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  return {
    configs,
    isLoading,
    error,
    reload: loadConfigs,
  };
}

